import { useCallback, useRef, useState } from 'react';

import {
    type EventData,
    type EventTrackingFunction,
    InteractionEvent,
} from '../types/eventTracking';

export const useOpenCalculatorWithTimer = ({
    onInteractionEvent,
    interactionEventData,
    onPressCalculatorPrimaryButton,
}: {
    onInteractionEvent?: EventTrackingFunction;
    interactionEventData?: Record<string, string | number | boolean>;
    onPressCalculatorPrimaryButton?: () => void;
}) => {
    const [isCalculatorOpen, setIsCalculatorOpen] = useState(false);
    const openTimestampRef = useRef<number | null>(null);

    const getOpenDurationMs = useCallback(() => {
        if (openTimestampRef.current == null) {
            return 'undefined';
        }
        return Date.now() - openTimestampRef.current;
    }, []);

    const handleOpenCalculator = useCallback(() => {
        openTimestampRef.current = Date.now();
        setIsCalculatorOpen(true);
        onInteractionEvent?.(
            InteractionEvent.MoneyMapEarningsCalculatorOpened,
            interactionEventData,
        );
    }, [onInteractionEvent, interactionEventData]);

    const handleCloseCalculator = useCallback(
        (additionalInteractionEventData: EventData) => {
            setIsCalculatorOpen(false);
            onInteractionEvent?.(
                InteractionEvent.MoneyMapEarningsCalculatorClosed,
                {
                    ...interactionEventData,
                    ...additionalInteractionEventData,
                    calculatorOpenDurationMs: getOpenDurationMs(),
                },
            );
        },
        [onInteractionEvent, interactionEventData],
    );

    const handleCalculatorPrimaryButtonPress = useCallback(
        (additionalInteractionEventData: EventData) => {
            onPressCalculatorPrimaryButton?.();
            onInteractionEvent?.(
                InteractionEvent.MoneyMapEarningsCalculatorCTAPressed,
                {
                    ...interactionEventData,
                    ...additionalInteractionEventData,
                    calculatorOpenDurationMs: getOpenDurationMs(),
                },
            );
            handleCloseCalculator(additionalInteractionEventData);
        },
        [onPressCalculatorPrimaryButton],
    );

    return {
        isCalculatorOpen,
        handleOpenCalculator,
        handleCloseCalculator,
        handleCalculatorPrimaryButtonPress,
    };
};
